import * as Sentry from '@sentry/react';
import {
  useMutation,
  useQueries,
  type QueryObserverResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { createInitiative } from '../../api/initiative-client/initiative-client';
import { useInitiatives, useKPIOverviewData } from '../../api/initiative-client/initiative-client.hooks';
import type {
  ExtendedInitiative,
  Initiative,
  KPIOverviewData,
  PreSaveInitiative,
} from '../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../api/initiative-client/initiative-client.type';
import type { Portfolio } from '../../api/portfolio-client/portfolio-client.type';
import { useEpics } from '../../api/process-client/process-client.hooks';
import type { Epic } from '../../api/tasks-client/task-client.type';
import { getWorkPeriodsMeasure } from '../../api/work-periods-client/work-periods-client';
import type {
  GetWorkPeriodsMeasurePayload,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../api/work-periods-client/work-periods-client.type';
import { TimeAllocationType, Transformer } from '../../api/work-periods-client/work-periods-client.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useStrategyStore } from '../../store/strategy-store/strategy-store';
import {
  setEpics,
  setInitiatives,
  setKPIOverviewData,
  setSelectedInitiative,
} from '../../store/strategy-store/strategy-store.actions';
import { timeAllocationTransformerMeasures } from '../process-analysis/comparison-view/comparison-view.hooks';
import { getContributingTeams } from './initiative-list/main-section/main-section.helpers';
import { filterInitiativesBySearchTerm } from './strategy-v2.helpers';
import type { InitiativeRecord } from './strategy-v2.type';
import { InitiativeState, Status } from './strategy-v2.type';

/**
 * Hook to create a new initiative mutation
 *
 * @param portfolio - The portfolio to create the initiative in, or null if no portfolio is selected
 * @param options - Optional mutation options to customize behavior
 * @returns A mutation result object containing the mutation function and state
 * @throws Error if no portfolio is selected when attempting to create
 */
const useCreateInitiativeMutation = (
  portfolio: Portfolio | null,
  options?: Partial<UseMutationOptions<Initiative, Error, PreSaveInitiative>>,
): UseMutationResult<Initiative, Error, PreSaveInitiative> => {
  return useMutation({
    mutationKey: ['createInitiative', portfolio],
    mutationFn: (initiative: PreSaveInitiative) =>
      portfolio ? createInitiative(portfolio.id, initiative) : Promise.reject('No portfolio selected'),
    ...options,
  } as UseMutationOptions<Initiative, Error, PreSaveInitiative>);
};

/**
 * Custom hook that fetches and manages strategy data
 *
 * This hook fetches initiatives and KPI overview data for the current portfolio,
 * updates the global store with the fetched data, and handles error cases.
 *
 * @returns {Object} Object containing query results and data
 * @returns {QueryObserverResult<Initiative[]>} initiativesQuery - Query result for initiatives
 * @returns {Initiative[]} initiatives - Array of initiatives
 * @returns {QueryObserverResult<KPIOverviewData>} kpiOverviewDataQuery - Query result for KPI overview data
 * @returns {KPIOverviewData | null} kpiOverviewData - KPI overview data or null if not available
 */
const useStrategyData = (): {
  initiativesQuery: QueryObserverResult<Initiative[]>;
  initiatives: Initiative[];
  kpiOverviewDataQuery: QueryObserverResult<KPIOverviewData>;
  kpiOverviewData: KPIOverviewData | null;
} => {
  const { initiativesQuery, initiatives } = useInitiativesQuery();
  const { kpiOverviewDataQuery, kpiOverviewData } = useKPIOverviewDataQuery();

  return {
    initiativesQuery,
    initiatives,
    kpiOverviewDataQuery,
    kpiOverviewData,
  };
};

/**
 * Custom hook that provides initiatives data and query results
 *
 * This hook fetches initiatives for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @returns {Object} Object containing query results and initiatives data
 * @returns {QueryObserverResult<Initiative[]>} initiativesQuery - Query result for initiatives
 * @returns {Initiative[]} initiatives - Array of initiatives
 */
const useInitiativesQuery = (): {
  initiativesQuery: QueryObserverResult<Initiative[]>;
  initiatives: Initiative[];
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedInitiative = useStrategyStore((state) => state.selectedInitiative);

  const { query: initiativesQuery, initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  useEffect(() => {
    if (initiativesQuery.isSuccess) {
      setInitiatives(initiatives);

      if (selectedInitiative) {
        const initiative = initiatives.find((initiative) => initiative.id === selectedInitiative.id);

        if (initiative) {
          setSelectedInitiative(initiative);
        }
      }
    }
  }, [initiatives, initiativesQuery.isSuccess, selectedInitiative]);

  useEffect(() => {
    if (initiativesQuery.error) {
      setInitiatives([]);
      Sentry.captureException(initiativesQuery.error);
    }
  }, [initiativesQuery.error]);

  return { initiativesQuery, initiatives };
};

/**
 * Custom hook that provides KPI overview data and query results
 *
 * This hook fetches KPI overview data for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @returns {Object} Object containing query results and KPI overview data
 * @returns {QueryObserverResult<KPIOverviewData>} kpiOverviewDataQuery - Query result for KPI overview data
 * @returns {KPIOverviewData | null} kpiOverviewData - KPI overview data or null if not available
 */
const useKPIOverviewDataQuery = (): {
  kpiOverviewDataQuery: QueryObserverResult<KPIOverviewData>;
  kpiOverviewData: KPIOverviewData | null;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const { query: kpiOverviewDataQuery, kpiOverviewData = null } = useKPIOverviewData(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<KPIOverviewData>);

  useEffect(() => {
    if (kpiOverviewDataQuery.isSuccess) {
      setKPIOverviewData(kpiOverviewData ?? null);
    }
  }, [kpiOverviewData, kpiOverviewDataQuery.isSuccess]);

  useEffect(() => {
    if (kpiOverviewDataQuery.error) {
      setKPIOverviewData(null);
      Sentry.captureException(kpiOverviewDataQuery.error);
    }
  }, [kpiOverviewDataQuery.error]);

  return { kpiOverviewDataQuery, kpiOverviewData };
};

/**
 * Custom hook that provides epics data and query results
 *
 * This hook fetches epics for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enabled=false] - Whether to enable the query
 * @returns {Object} Object containing query results and epics data
 * @returns {QueryObserverResult<Epic[]>} epicsQuery - Query result for epics
 * @returns {Epic[]} epics - Array of epics
 */
const useEpicsQuery = ({
  enabled = false,
}: {
  enabled?: boolean;
} = {}): {
  epicsQuery: QueryObserverResult<Epic[]>;
  epics: Epic[];
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const { query: epicsQuery, epics = [] } = useEpics(
    { portfolioId: portfolio?.id || null, projectId: null, subprojectId: null },
    {
      enabled: !!portfolio?.id && enabled,
    } as UseQueryOptions<Epic[]>,
  );

  useEffect(() => {
    if (epicsQuery.isSuccess) {
      setEpics(epics);
    }
  }, [epics, epicsQuery.isSuccess]);

  useEffect(() => {
    if (epicsQuery.error) {
      setEpics([]);
      Sentry.captureException(epicsQuery.error);
    }
  }, [epicsQuery.error]);

  return { epicsQuery, epics };
};

/**
 * Custom hook that provides filtered initiatives based on search term and filters
 *
 * This hook filters initiatives based on the current search term, source filters,
 * and status filters from the strategy store. When a search term is provided and
 * search is enabled, it uses search results instead of global initiatives.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enableSearch=true] - Whether to enable search functionality
 * @returns {Object} Object containing filtered initiatives and loading state
 * @returns {Initiative[]} initiatives - Array of filtered initiatives
 */
const useFilteredInitiatives = ({
  enableSearch = true,
}: {
  enableSearch?: boolean;
} = {}): {
  initiatives: InitiativeRecord[];
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teams = useGlobalStore((state) => state.teams);
  const globalInitiatives = useStrategyStore((state) => state.initiatives);
  const searchTerm = useStrategyStore((state) => state.searchTerm);
  const sourceFilters = useStrategyStore((state) => state.filters.source);
  const stateFilters = useStrategyStore((state) => state.filters.state);
  const statusFilters = useStrategyStore((state) => state.filters.status);
  const isActiveFilters = useStrategyStore((state) => state.filters.is_active);

  const initiatives = useMemo(
    () =>
      searchTerm && enableSearch ? filterInitiativesBySearchTerm(globalInitiatives, searchTerm) : globalInitiatives,
    [globalInitiatives, searchTerm, enableSearch],
  );

  // Filter by source
  let filteredInitiatives = initiatives.filter((initiative) => sourceFilters.includes(initiative.source));

  // Filter by state (no epics and no end date)
  // Filter initiatives with no epics
  if (stateFilters.includes(InitiativeState.NoEpics)) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => initiative.epic_count === 0);
  }

  // Filter initiatives with no end date
  if (stateFilters.includes(InitiativeState.NoEndDate)) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => initiative.no_end_date === true);
  }

  // Filter by status
  if (statusFilters.length > 0) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      if (initiative.status?.by_tasks?.status) {
        return statusFilters.includes(initiative.status?.by_tasks?.status);
      } else {
        if (
          statusFilters.includes(Status.Unknown) &&
          (initiative.status?.by_tasks?.status === InitiativeStatus.Empty || !initiative.status?.by_tasks?.status)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  // Filter by active/inactive state
  if (isActiveFilters.length > 0) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      if (portfolio?.use_end_date) {
        const isActive =
          isActiveFilters.includes(InitiativeState.Active) && dayjs(initiative.end_date).isAfter(dayjs());
        const isInactive =
          isActiveFilters.includes(InitiativeState.Inactive) && dayjs(initiative.end_date).isBefore(dayjs());
        return isActive || isInactive;
      } else {
        const isActive = isActiveFilters.includes(InitiativeState.Active) && initiative.is_active;
        const isInactive = isActiveFilters.includes(InitiativeState.Inactive) && !initiative.is_active;
        return isActive || isInactive;
      }
    });
  }

  const formattedInitiatives = filteredInitiatives.map((initiative) => ({
    ...initiative,
    contributing_teams_label: getContributingTeams(initiative, teams),
  }));

  return { initiatives: formattedInitiatives };
};

/**
 * Hook to check if any initiative filters are currently applied
 *
 * @returns {boolean} True if any source, state, status or active/inactive filters are selected, false otherwise
 */
const useFiltersApplied = (): boolean => {
  const sourceFilters = useStrategyStore((state) => state.filters.source);
  const stateFilters = useStrategyStore((state) => state.filters.state);
  const statusFilters = useStrategyStore((state) => state.filters.status);
  const isActiveFilters = useStrategyStore((state) => state.filters.is_active);

  return sourceFilters.length > 0 || stateFilters.length > 0 || statusFilters.length > 0 || isActiveFilters.length > 0;
};

/**
 * Hook to fetch measure data for a specific initiative
 *
 * @param {Object} params - The params object
 * @param {ExtendedInitiative} params.initiative - The initiative to fetch measure data for
 * @param {MeasureMetadata[]} params.measureMetadata - The measure metadata to fetch data for
 * @returns {Object} Object containing:
 *   - data: The measure data
 *   - isLoading: Whether the measure data is loading
 */
const useInitiativeMeasureData = ({
  initiative,
  measureMetadata,
}: {
  initiative: ExtendedInitiative | null;
  measureMetadata: MeasureMetadata[];
}): { data: (MeasureDataResponse | undefined)[]; isLoading: boolean } => {
  const queryConfigs = measureMetadata.map((measure) => {
    const isCustom = measure.is_custom ?? false;
    const payload: GetWorkPeriodsMeasurePayload = {
      start_date: initiative?.start_date ? dayjs(initiative.start_date).format('YYYY-MM-DD') : undefined,
      end_date: initiative?.end_date ? dayjs(initiative.end_date).format('YYYY-MM-DD') : undefined,
      initiative_id: initiative?.id,
      measure_name: [measure.measure_name],
      force_recalculation: false,
      is_custom: isCustom,
      time_allocation_type: TimeAllocationType.BiWeekly,
    };
    if (isCustom) {
      payload.transformer = Transformer.ReturnFirstMetricOnly;
    } else if (timeAllocationTransformerMeasures.includes(measure.measure_name)) {
      payload.transformer = Transformer.TimeAllocationValuesOnly;
    }
    return {
      queryKey: ['initiative-measure-data', initiative?.id, measure.measure_name] as const,
      queryFn: () => getWorkPeriodsMeasure<MeasureDataResponse>(payload),
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
      retry: false,
    };
  });

  const queries = useQueries({ queries: queryConfigs });
  const isLoading = queries.some((query) => query.isLoading);
  const data = queries.map((query) => query.data);
  return { data, isLoading };
};

export {
  useCreateInitiativeMutation,
  useEpicsQuery,
  useFilteredInitiatives,
  useFiltersApplied,
  useInitiativeMeasureData,
  useStrategyData,
};
